import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  FileDetailsConstituent,
  FileDetailsRecord,
  FileDetailsVersion,
  FileDependencyItem,
} from "@ServerTypes/fileOperations/fileOperations.schemas";
import type { ApiPostResponse } from "@Services/axiosShared";
import type { UpdateFileUnitsRequest } from "@ServerTypes/queries/queries.schema";

/** `/fileOperations/getFileDetails` records, keyed by the id of the version each was opened by. */
type FilesDataState = Record<string, FileDetailsRecord>;
type FileDependenciesState = Record<string, FileDependencyItem[]>;
/** `/getResourcesUnderWhichFilesShared`'s body verbatim: the transmittal rows, with no status envelope. */
type SharedResourcesResponse = ApiPostResponse<"/fileOperations/getResourcesUnderWhichFilesShared">;
type FilesSharedUnderResourcesState = Record<string, SharedResourcesResponse>;

const filesDataSlice = createSlice({
  name: "filesData",
  initialState: {} as FilesDataState,
  reducers: {
    setFilesData: (state, action: PayloadAction<Record<string, FileDetailsRecord>>) => ({
      ...state,
      ...action.payload,
    }),
    resetFilesData: (state) => state,
    modifyFileUnitsData: (
      state,
      action: PayloadAction<{ fileVersionId: string; unitsNew: UpdateFileUnitsRequest["unitsNew"] }>,
    ) => {
      const { fileVersionId, unitsNew } = action.payload;
      const fileId = state[fileVersionId]?._id;
      if (fileId === undefined) return state;
      // `Units` is the file's, and a file is held once for every version it was opened by.
      return Object.fromEntries(
        Object.entries(state).map(([key, file]) => [
          key,
          file._id === fileId ? { ...file, Units: { ...file.Units, ...unitsNew } } : file,
        ]),
      );
    },
  },
});

const fileDependenciesSlice = createSlice({
  name: "fileDependencies",
  initialState: {} as FileDependenciesState,
  reducers: {
    setFileDependencies: (state, action: PayloadAction<{ fileVersionId: string; data: FileDependencyItem[] }>) => {
      const { fileVersionId, data } = action.payload;
      state[fileVersionId] = [...data];
    },
  },
});

const filesSharedUnderResourcesSlice = createSlice({
  name: "filesSharedUnderResources",
  initialState: {} as FilesSharedUnderResourcesState,
  reducers: {
    setResourcesUnderWhichFilesShared: (
      _state,
      action: PayloadAction<{ fileId: string; data: SharedResourcesResponse }>,
    ) => ({
      [action.payload.fileId]: action.payload.data,
    }),
  },
});

export interface DependencyQuerySettings {
  searchParameter: string;
  searchCondition: string;
  multiDependencyPreference: string;
}

type DependencyQuerySettingsState = Record<string, DependencyQuerySettings | null>;

const dependencyQuerySettingsSlice = createSlice({
  name: "dependencyQuerySettings",
  initialState: {} as DependencyQuerySettingsState,
  reducers: {
    setDependencyQuerySettings: (
      state,
      action: PayloadAction<{ fileVersionId: string; data: DependencyQuerySettings | null }>,
    ) => {
      state[action.payload.fileVersionId] = action.payload.data;
    },
  },
});

const currentFileVersionIdSlice = createSlice({
  name: "currentFileVersionId",
  initialState: "",
  reducers: {
    setFileVersionId: (_state, action: PayloadAction<string>) => action.payload,
  },
});

/**
 * Read one entry out of the `filesData` slice by an id that may be absent.
 *
 * The viewer's `fileVersionId` is `undefined` while no file is mounted, and `Record<string, T>`
 * cannot be indexed by `string | undefined`. A missing id simply has no entry, which is what
 * every caller already handles.
 */
export const selectFileVersionData = (
  filesData: FilesDataState,
  fileVersionId: string | undefined,
): FileDetailsRecord | undefined => (fileVersionId === undefined ? undefined : filesData[fileVersionId]);

/** The version `fileVersionId` names, out of its file's record. */
export const versionOf = (
  file: FileDetailsRecord | undefined,
  fileVersionId: string | undefined,
): FileDetailsVersion | undefined =>
  fileVersionId === undefined ? undefined : file?.versions?.find((version) => version._id === fileVersionId);

/**
 * Constituent version ids, as a merged-model version names them in `constituentFileIds` (the models it loads) or
 * `constituentFiles` (the sources it was merged from), each resolved through the record's `constituentFiles` to
 * that version and the file holding it, in the given order. An id the record carries no version for is left out.
 */
export const resolveConstituents = (
  file: Pick<FileDetailsRecord, "constituentFiles"> | undefined,
  constituentVersionIds: readonly string[] | null | undefined,
): { file: FileDetailsConstituent; version: FileDetailsVersion }[] =>
  (constituentVersionIds ?? []).flatMap((constituentVersionId) => {
    for (const constituent of file?.constituentFiles ?? []) {
      const version = constituent.versions.find((entry) => entry._id === constituentVersionId);
      if (version) return [{ file: constituent, version }];
    }
    return [];
  });

/** What the merged-model version `fileVersionId` loads: its `constituentFileIds`, resolved. */
export const constituentsOf = (
  file: FileDetailsRecord | undefined,
  fileVersionId: string | undefined,
): { file: FileDetailsConstituent; version: FileDetailsVersion }[] =>
  resolveConstituents(file, versionOf(file, fileVersionId)?.constituentFileIds);

export const { setFilesData: setFilesDataAction, resetFilesData, modifyFileUnitsData } = filesDataSlice.actions;

export const { setFileDependencies: setFileDependenciesAction } = fileDependenciesSlice.actions;

export const { setDependencyQuerySettings: setDependencyQuerySettingsAction } = dependencyQuerySettingsSlice.actions;

export const { setResourcesUnderWhichFilesShared: setResourcesUnderWhichFilesSharedAction } =
  filesSharedUnderResourcesSlice.actions;

export const { setFileVersionId: setFileVersionIdAction } = currentFileVersionIdSlice.actions;

export const setFilesData = filesDataSlice.reducer;
export const setFileDependencies = fileDependenciesSlice.reducer;
export const dependencyQuerySettings = dependencyQuerySettingsSlice.reducer;
export const filesSharedUnderResources = filesSharedUnderResourcesSlice.reducer;
export const currentFileVersionId = currentFileVersionIdSlice.reducer;
